import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import {
  FileXlsIcon, DownloadSimpleIcon, UploadSimpleIcon, XIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * <ExcelBulkActions />
 * Botonera compacta para bulk Excel: Descargar plantilla · Exportar · Importar.
 *
 * Props:
 *  - templateUrl: URL relativa al backend para descargar plantilla (ej: "/suppliers/import/template")
 *  - exportUrl:   URL relativa para exportar todos (ej: "/suppliers/export")
 *  - importUrl:   URL relativa para importar (ej: "/suppliers/import")
 *  - templateFilename, exportFilename: nombres sugeridos al descargar
 *  - resultLabels: { created?: string, updated?: string } — customiza los labels del summary
 *  - onImported: (data) => void — callback tras import exitoso
 *  - testidPrefix: prefijo para data-testids (ej: "suppliers")
 */
export default function ExcelBulkActions({
  templateUrl,
  exportUrl,
  importUrl,
  templateFilename = "plantilla.xlsx",
  exportFilename = "export.xlsx",
  resultLabels = { created: "Creados", updated: "Actualizados" },
  onImported,
  testidPrefix = "excel",
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const { user } = useAuth();
  // Importar desde Excel: exclusivo del Administrador General (el servidor también lo exige)
  const esAdmin = user?.role === "admin" || Boolean(user?.effective?.is_admin);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    window.URL.revokeObjectURL(url);
  };

  const doTemplate = async () => {
    setBusy(true);
    try {
      const res = await api.get(templateUrl, { responseType: "blob" });
      downloadBlob(res.data, templateFilename);
      toast.success("Plantilla descargada");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const doExport = async () => {
    setBusy(true);
    try {
      const res = await api.get(exportUrl, { responseType: "blob" });
      downloadBlob(res.data, exportFilename);
      toast.success("Exportación descargada");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(importUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const okCount = data.imported ?? data.creados ?? 0;
      const upCount = data.actualizados ?? 0;
      const errCount = (data.errors || data.errores || []).length;
      toast.success(`Importación completa: ${okCount} filas${upCount ? ` · ${upCount} actualizadas` : ""}${errCount ? ` · ${errCount} errores` : ""}`);
      onImported && onImported(data);
    } catch (er) { toast.error(apiErr(er)); }
    finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const rm = useModalForm({ abierto: Boolean(result), onClose: () => setResult(null) });
  return (
    <>
      <div className="flex flex-wrap gap-1 border border-border rounded-md p-0.5" data-testid={`${testidPrefix}-excel-toolbox`}>
        <button
          onClick={doTemplate} disabled={busy}
          title="Descargar plantilla Excel"
          className="px-2.5 py-1.5 text-xs text-text-secondary hover:text-agro-green flex items-center gap-1 disabled:opacity-50 rounded"
          data-testid={`${testidPrefix}-template-btn`}
        >
          <FileXlsIcon size={14} /> Plantilla
        </button>
        <button
          onClick={doExport} disabled={busy}
          title="Exportar todo a Excel"
          className="px-2.5 py-1.5 text-xs text-text-secondary hover:text-agro-green flex items-center gap-1 disabled:opacity-50 rounded"
          data-testid={`${testidPrefix}-export-btn`}
        >
          <DownloadSimpleIcon size={14} /> Exportar
        </button>
        {esAdmin && <label className="px-2.5 py-1.5 text-xs text-text-secondary hover:text-agro-yellow flex items-center gap-1 cursor-pointer rounded">
          <UploadSimpleIcon size={14} /> Importar
          <input
            type="file" accept=".xlsx"
            onChange={doImport} disabled={busy}
            className="hidden"
            data-testid={`${testidPrefix}-import-input`}
          />
        </label>}
      </div>

      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...rm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid={`${testidPrefix}-import-result`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg">Resultado de importación</h3>
              <button aria-label="Cerrar" onClick={rm.cerrar} className="text-text-muted" data-testid={`${testidPrefix}-close-result`}>
                <XIcon size={20} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-app border border-border rounded-md p-3 text-center">
                <div className="text-[10px] uppercase text-text-muted tracking-wider">{resultLabels.created || "Importados"}</div>
                <div className="font-heading text-2xl text-agro-green">{result.imported ?? result.creados ?? 0}</div>
              </div>
              <div className="bg-app border border-border rounded-md p-3 text-center">
                <div className="text-[10px] uppercase text-text-muted tracking-wider">{resultLabels.updated || "Actualizados"}</div>
                <div className="font-heading text-2xl text-agro-yellow">{result.actualizados ?? 0}</div>
              </div>
              <div className="bg-app border border-border rounded-md p-3 text-center">
                <div className="text-[10px] uppercase text-text-muted tracking-wider">Errores</div>
                <div className="font-heading text-2xl text-red-400">{(result.errors || result.errores || []).length}</div>
              </div>
            </div>
            {result.message && (
              <div className="text-sm text-text-secondary mb-3 bg-app/60 border border-border/60 rounded px-3 py-2">
                {result.message}
              </div>
            )}
            {(result.errors || result.errores || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Detalle de errores</div>
                <ul className="space-y-1 max-h-64 overflow-y-auto text-xs bg-app/40 border border-border/40 rounded p-2">
                  {(result.errors || result.errores || []).slice(0, 200).map((err, idx) => (
                    <li key={idx} className="text-red-300">
                      <b>Fila {err.row ?? err.fila ?? "?"}:</b> {err.error ?? err.mensaje ?? JSON.stringify(err)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
